import { z } from 'zod';

export const SHOW_OPTIONS_TITLE_MAX_LENGTH = 160;
export const SHOW_OPTION_LABEL_MAX_LENGTH = 120;
export const SHOW_OPTION_SUBLABEL_MAX_LENGTH = 180;
export const SHOW_OPTIONS_MIN_ITEMS = 2;
export const SHOW_OPTIONS_MAX_ITEMS = 5;

const boundedTrimmedCopy = (maxLength: number) =>
  z
    .string()
    .min(1)
    .max(maxLength)
    .refine((value) => value === value.trim(), 'copy must not have surrounding whitespace');

export const showOptionIdSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9_-]{0,63}$/);

export const showOptionSchema = z
  .object({
    id: showOptionIdSchema,
    label: boundedTrimmedCopy(SHOW_OPTION_LABEL_MAX_LENGTH),
    sublabel: boundedTrimmedCopy(SHOW_OPTION_SUBLABEL_MAX_LENGTH).optional(),
  })
  .strict();

export const showOptionsParametersSchema = z
  .object({
    title: boundedTrimmedCopy(SHOW_OPTIONS_TITLE_MAX_LENGTH),
    options: z.array(showOptionSchema).min(SHOW_OPTIONS_MIN_ITEMS).max(SHOW_OPTIONS_MAX_ITEMS),
  })
  .strict()
  .superRefine(({ options }, context) => {
    const seen = new Set<string>();
    options.forEach((option, index) => {
      if (seen.has(option.id)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['options', index, 'id'],
          message: 'option ids must be unique within one invocation',
        });
      }
      seen.add(option.id);
    });
  });

const selectedShowOptionResultSchema = z
  .object({
    status: z.literal('selected'),
    option_id: showOptionIdSchema,
    label: boundedTrimmedCopy(SHOW_OPTION_LABEL_MAX_LENGTH),
  })
  .strict();

const timedOutShowOptionsResultSchema = z.object({ status: z.literal('timed_out') }).strict();

export const showOptionsResultSchema = z.discriminatedUnion('status', [
  selectedShowOptionResultSchema,
  timedOutShowOptionsResultSchema,
]);

export type ShowOption = z.infer<typeof showOptionSchema>;
export type ShowOptionsParameters = z.infer<typeof showOptionsParametersSchema>;
export type ShowOptionsResult = z.infer<typeof showOptionsResultSchema>;

export function parseTrustedShowOptionsResult(
  invokedParameters: unknown,
  candidateResult: unknown,
): ShowOptionsResult {
  const parameters = showOptionsParametersSchema.parse(invokedParameters);
  const result = showOptionsResultSchema.parse(candidateResult);
  if (result.status === 'timed_out') return result;

  const invokedOption = parameters.options.find((option) => option.id === result.option_id);
  if (!invokedOption || invokedOption.label !== result.label) {
    throw new Error('selected show_options result must match one exact invoked id and label');
  }
  return result;
}

export function isTrustedShowOptionsResult(
  invokedParameters: unknown,
  candidateResult: unknown,
): candidateResult is ShowOptionsResult {
  try {
    parseTrustedShowOptionsResult(invokedParameters, candidateResult);
    return true;
  } catch {
    return false;
  }
}
